import { Box, Grid, Typography } from '@mui/material';
import {
  AutocompleteItem,
  AutocompleteMode,
  SearchBar,
  SearchBarVariant,
  SearchParams,
} from '@prismamedia/one-components';
import { FC, useEffect, useRef, useState } from 'react';
import { TagsProps } from '../../';
import {
  ArticleFormat,
  BrandKey,
  GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag,
  GetSearchTvProgramQuery,
  GetSearchTvProgramQuery_query_searchTvPrograms_TvProgram,
  GetSearchTvProgramQueryVariables,
  GetSuggestedTagsListQuery_query_suggestTags_Tag,
  TagType,
  TvSearchContextType,
} from '../../../../__generated__/queries-web';
import { useSearchSuggestedTagsGetter } from '../../../../apollo/queries/getSuggestedTagsList.web.graphql';
import { TagItem } from '../../TagItem';
import { useEditableTag } from '../../useEditTag';
import { getSuggestedTagsArticleParams } from '../../utils';
import { useStyles as useSharedStyles } from '../styles';
import {
  ALLOWED_TVBY_TYPES,
  ARTICLE_ENHANCEMENT_PROGRAMS_TITLE,
  NO_PROGRAM_SELECTED_TITLE,
  PROGRAM_SEARCH_BAR_PLACEHOLDER,
  PROVIDER_LABEL,
} from './constants';
import { useLazyQuery } from '@apollo/client';
import { GET_SEARCH_TV_PROGRAM } from '../../../../apollo/queries/getSearchTvProgram.web.graphql';

const getCheckedTypesList = (searchParams: SearchParams): TagType[] =>
  Object.keys(searchParams).reduce((acc: TagType[], currValue: string) => {
    const isProviderAllowed =
      searchParams[currValue] === true &&
      ALLOWED_TVBY_TYPES.indexOf(currValue as TagType) > -1;

    if (isProviderAllowed) {
      // eslint-disable-next-line fp/no-mutating-methods
      acc.push(currValue as TagType);
    }

    return acc;
  }, []);

interface ProgramsProps extends Pick<TagsProps, 'article' | 'onTagsChange'> {
  brandKey: BrandKey;
  field: string;
  suggestedTagList: GetSuggestedTagsListQuery_query_suggestTags_Tag[];
  tags: GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag[];
}

const Programs: FC<ProgramsProps> = ({
  article,
  brandKey,
  field,
  onTagsChange,
  tags: selectedTags,
}) => {
  const isFirstRender = useRef(true);
  const [getSearchTvProgram] = useLazyQuery<
    GetSearchTvProgramQuery,
    GetSearchTvProgramQueryVariables
  >(GET_SEARCH_TV_PROGRAM, {
    fetchPolicy: 'no-cache',
  });

  const searchSuggestedTags = useSearchSuggestedTagsGetter();

  const {
    newTags,
    handlers: { handleAddTag, handleDeleteTag },
  } = useEditableTag<
    GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag
  >(selectedTags, brandKey);

  const [searchParams, setSearchParams] = useState<SearchParams>({
    search: '',
    ...ALLOWED_TVBY_TYPES.reduce((acc, currValue) => {
      // eslint-disable-next-line immutable/no-mutation
      acc[currValue] = true;
      return acc;
    }, {} as Partial<SearchParams>),
  });

  const selectedProgramList = selectedTags.filter(({ type }) => {
    return type === TagType.TvProgram;
  });

  const allowedTypes = getCheckedTypesList(searchParams);
  const sharedClasses = useSharedStyles({
    selectedTagListLength: selectedProgramList.length,
  });

  const getFilteredProgramList = async (
    search: string,
  ): Promise<AutocompleteItem[] | undefined> => {
    setSearchParams((currSearchParams) => ({
      ...currSearchParams,
      search,
    }));

    if (!search.length) {
      // Get suggested programs tags
      return searchSuggestedTags({
        article: getSuggestedTagsArticleParams(article),
        where: {
          type_in: allowedTypes,
        },
      }).then((data) => {
        const tagList = data || [];

        return tagList.map((tag) => ({
          label: tag.title,
          id: tag.id,
          title: tag.title,
          type: tag.type,
          relationId: tag.relationId,
        }));
      });
    }

    // Get programs tags based on field value
    const tvProgramList = await getSearchTvProgram({
      variables: {
        query: search,
        context:
          article?.format === ArticleFormat.Video
            ? TvSearchContextType.Video
            : TvSearchContextType.Rich,
      },
    });

    if (tvProgramList.data?.searchTvPrograms) {
      const groupedTagsByBoneUUID = tvProgramList.data?.searchTvPrograms.reduce<
        Record<
          string,
          GetSearchTvProgramQuery_query_searchTvPrograms_TvProgram[]
        >
      >((acc, program) => {
        return {
          ...acc,
          [program.boneUUID]: [...(acc[program.boneUUID] ?? []), program],
        };
      }, {});

      const formatDate = (date: string) => {
        if (!date) {
          return '';
        }
        const dateObj = new Date(date);
        return dateObj
          .toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone: 'Europe/Paris',
          })
          .replace(',', '')
          .replace(' ', ' ');
      };

      const result = Object.values(groupedTagsByBoneUUID).flatMap(
        (programsByBoneUUID) => {
          if (programsByBoneUUID.length === 1) {
            const [program] = programsByBoneUUID;
            return {
              ...program,
              type: TagType.TvProgram,
              title: `${program.title} {${program.id}}`,
              label: `${program.title} ${
                program.startedAt
                  ? `- ${formatDate(program.startedAt as string)} ${
                      program.channel
                    }`
                  : ``
              } {${program.id}}`,
              relationId: program.boneUUID,
            };
          }

          const [mainProgram] = programsByBoneUUID;
          return [
            {
              id: mainProgram.id,
              type: TagType.TvProgram,
              label: `${mainProgram.title} ${mainProgram.channel} {${mainProgram.id}}`,
              title: `${mainProgram.title} {${mainProgram.id}}`,
              relationId: mainProgram.boneUUID,
            },
            ...programsByBoneUUID.map((program) => {
              return {
                ...program,
                type: TagType.TvProgram,
                label: `---- Diffusion le ${formatDate(
                  program.startedAt as string,
                )} ${program.channel} ---- {${program.id}}`,
                title: `${mainProgram.title} {${program.id}}`,
                relationId: program.boneUUID,
              };
            }),
          ];
        },
      );
      return result;
    }

    return [];
  };

  useEffect(() => {
    if (isFirstRender.current) {
      // eslint-disable-next-line immutable/no-mutation
      isFirstRender.current = false;
      return;
    }

    newTags && onTagsChange(newTags, field);
  }, [field, newTags, onTagsChange]);

  return (
    <Grid
      alignItems="flex-end"
      className={sharedClasses.wrapper}
      container
      justifyContent="space-between"
    >
      <Grid item xs={4}>
        <Typography className={sharedClasses.subTitle} component="h3">
          {ARTICLE_ENHANCEMENT_PROGRAMS_TITLE}{' '}
          <span>({selectedProgramList.length})</span>
        </Typography>
      </Grid>

      <Grid item xs={8}>
        <SearchBar
          autocomplete={{
            fetchList: getFilteredProgramList,
            listHeader: undefined,
            mode: AutocompleteMode.MULTIPLE,
            onSelect: (value) => {
              value && handleAddTag(value);
            },
          }}
          chipAdvancedParamLabel={PROVIDER_LABEL}
          fetchListMinValueLength={0}
          placeholder={PROGRAM_SEARCH_BAR_PLACEHOLDER}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          variant={SearchBarVariant.SECONDARY}
        />
      </Grid>
      <Box className={sharedClasses.innerWrapper} component="section">
        {selectedProgramList.map((program) => (
          <TagItem
            {...program}
            key={program.title}
            onDelete={handleDeleteTag}
          />
        ))}

        {!selectedProgramList.length && (
          <Typography component="p">{NO_PROGRAM_SELECTED_TITLE}</Typography>
        )}
      </Box>
    </Grid>
  );
};

export { Programs };
